/**
 * Walk photos — the last card of a walk in the Review queue (Claude Design
 * "Review · walk photos", LED-1154). The phone's version is the review deck's
 * Walk photos card (`backend/src/frontend/pages/visit/WalkPhotosCard.tsx`,
 * design 7) with the photo viewer's Make it a (8d); this is the same card,
 * laid out for the console.
 *
 *  - NOT ATTACHED · N: the photos still on this step's Photos cards, four
 *    across, square, each with its time. Linked projects use MinusCircle to
 *    leave a photo out (`excludedMediaIds`); RESTORE brings it back. Unlinked
 *    projects use trash with confirmation to delete the photo immediately.
 *  - Clicking a photo (or one already on a record) opens the full-screen
 *    viewer (`WalkPhotoViewer`, design "Led AI - Web Photo Viewer"): the photo
 *    large, its description (saved on blur), its location (the project's
 *    location directory, saved through `setWalkPhotoLocation`), and MAKE IT A —
 *    Procore: Observation, Punch item, Task, Daily log entry → which section?;
 *    Autodesk: Issue → Observation / Punch List; unlinked: the first three.
 *  - Make it a creates the pending record at once (`convertWalkPhoto`, with
 *    the design's literal starting title — "New observation", "New manpower
 *    entry"), closes the viewer and says so in a toast with UNDO; the queue
 *    re-reads and shows the record as a card above this one, and the photo
 *    moves to ALREADY ON RECORDS tagged NEW OBS / NEW PUNCH / … . The record
 *    card's "Made from a walk photo" strip (`WalkPhotoOrigin`) carries the
 *    Undo too.
 *  - ALREADY ON RECORDS · N: every other photo of the walk, tagged by the
 *    record it is on.
 *  - Where the rest go: Procore and unlinked "Daily log + gallery" or
 *    "Gallery only"; Autodesk "Daily report + files" or "Files only" (the
 *    phone's words) — defaulted from the reader's role as the phone does
 *    (`walkPhotosDefaultDestination`).
 *  - Footer as on every record card: Later (the Photos cards are saved for
 *    later), MinusCircle on linked projects (send none, keep all in Led AI),
 *    or trash on unlinked projects (delete these photos, after a confirmation
 *    dialog — the phone's `WalkPhotoDeleteDialog`). Accept sends linked
 *    photos; Save saves unlinked photos.
 *
 * Every integration word comes from `syncSource` (LED-763): Autodesk speaks
 * ACC — the daily report, Issues, no Task, no daily log entry.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Camera, Check, List, Search, Trash2, MinusCircle, X } from "lucide-react";
import { PhotoImage } from "../components/PhotoImage";
import { ConfirmDialog } from "../components/ConfirmDialog";
import {
  convertWalkPhoto,
  fetchItemEditOptions,
  fetchWalkPhotos,
  postReviewDecision,
  submitWalkPhotos,
  undoWalkPhotoConvert,
  updateWalkPhotoDescription,
  updateWalkPhotoLocation,
  type ConsoleWalkPhoto,
  type ItemEditDirectoryOption,
  type WalkPhotoConvertKind,
  type WalkPhotoDestination,
  type WalkPhotoLocation,
} from "../api";
import { useResource } from "../useResource";
import { integrationName, type SyncSource } from "../lib/integrationName";
import {
  walkPhotoTileTime,
  walkPhotosCardTitle,
  walkPhotosDefaultDestination,
} from "../../../../backend/src/frontend/pages/visit/walkPhotosModel";
import { FONT_MONO, FONT_TEXT, WPC, WPV } from "../tokens";
import { SkeletonBlock } from "../components/Skeleton";
import {
  forgetWalkPhotoOrigin,
  publishWalkPhotoOrigins,
  setWalkPhotosOutcome,
  useWalkPhotosOutcome,
  type WalkPhotoOrigin,
  type WalkPhotosOutcome,
} from "./WalkPhotoOrigin";
import {
  ISSUE_SUBTYPES,
  RECORD_TONE,
  RecordIcon,
  WalkPhotoViewer,
  recordTagOf,
  recordTagOfKind,
  type WalkPhotosIntegration,
} from "./WalkPhotoViewer";

const MONO: CSSProperties = { fontFamily: FONT_MONO, fontWeight: 700, textTransform: "uppercase" };
const LABEL: CSSProperties = { ...MONO, fontSize: 9, letterSpacing: "1.3px", color: WPC.label };
const HAIRLINE = "1px solid rgba(20,17,12,.07)";
const OUTLINE = "1px solid rgba(20,17,12,.14)";
const CARD_SHADOW = "0 1px 0 rgba(20,17,12,.04),0 10px 28px rgba(20,17,12,.06)";
const POP = "0 16px 40px rgba(10,10,10,.18),0 0 0 1px rgba(10,10,10,.07)";

/** A photo still under review: on a Photos card nobody has accepted. */
export function isReviewableWalkPhoto(photo: ConsoleWalkPhoto): boolean {
  return photo.home?.kind === "photos" && !photo.home.approved;
}

/** The card's integration: Procore, Autodesk, or an unlinked project. */
export function walkPhotosIntegrationFor(syncSource: SyncSource | undefined): WalkPhotosIntegration {
  return syncSource === "procore" || syncSource === "autodesk" ? syncSource : "local";
}

// ── Make it a ────────────────────────────────────────────────────────────

/** "Daily log · which section?" — the design's list, in its order. The keys
 *  are `backend/`'s `WALK_PHOTO_LOG_SECTIONS`. */
export const LOG_SECTIONS: Array<{ key: string; label: string }> = [
  { key: "manpower", label: "Manpower" },
  { key: "deliveries", label: "Deliveries" },
  { key: "equipment", label: "Equipment" },
  { key: "inspections", label: "Inspections" },
  { key: "visitors", label: "Visitors" },
  { key: "safety_violations", label: "Safety violations" },
  { key: "delays", label: "Delays" },
  { key: "quantities", label: "Quantities" },
  { key: "waste", label: "Waste" },
  { key: "notes", label: "Notes" },
];

/** The new record's literal starting title (design: "New observation",
 *  "New punch item", "New task", "New {section} entry"). */
export function newRecordTitle(kind: WalkPhotoConvertKind, sectionLabel?: string): string {
  if (kind === "observation") return "New observation";
  if (kind === "punch") return "New punch item";
  if (kind === "task") return "New task";
  return `New ${(sectionLabel ?? "Notes").toLowerCase()} entry`;
}

/** What the toast says the photo became: "observation", "punch item",
 *  "daily log · manpower"; under Autodesk "issue · observation". */
export function convertedLabel(kind: WalkPhotoConvertKind, integration: WalkPhotosIntegration, sectionLabel?: string): string {
  if (integration === "autodesk") {
    const sub = ISSUE_SUBTYPES.find((s) => s.kind === kind);
    if (sub) return sub.label.toLowerCase();
  }
  if (kind === "observation") return "observation";
  if (kind === "punch") return "punch item";
  if (kind === "task") return "task";
  return `daily log · ${(sectionLabel ?? "Notes").toLowerCase()}`;
}

// ── formatting ───────────────────────────────────────────────────────────

function splitPath(location: { name: string; path?: string }): { pre: string; leaf: string } {
  const parts = (location.path || location.name).split(/\s*(?:›|>|\/)\s*/).filter(Boolean);
  if (parts.length <= 1) return { pre: "", leaf: location.name };
  return { pre: `${parts.slice(0, -1).join(" › ")} › `, leaf: parts[parts.length - 1] };
}

/** "OCT 19, 2026 · 1:10A" — the walk's day and its last photo. */
function stampOf(day: string | null | undefined, lastPhoto: string | undefined): string {
  const date = day ?? lastPhoto;
  if (!date) return "";
  const d = new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }).toUpperCase();
  return lastPhoto ? `${d} · ${walkPhotoTileTime(lastPhoto)}` : d;
}

/** "Oct 19" — the daily log the photos would land on. */
function shortDay(day: string | null | undefined): string {
  return day ? new Date(day).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
}

/** Where the photos not on a record go — the phone's words per integration
 *  (`WalkPhotosCard`'s `dests`). */
export function walkPhotosDestinations(
  integration: WalkPhotosIntegration,
  day: string | null | undefined,
): Array<{ id: WalkPhotoDestination; label: string; sub: string }> {
  const date = shortDay(day);
  if (integration === "autodesk") {
    return [
      { id: "log", label: "Daily report + files", sub: date ? `${date} report · Files` : "Daily report · Files" },
      { id: "gallery", label: "Files only", sub: "Autodesk Files" },
    ];
  }
  return [
    { id: "log", label: "Daily log + gallery", sub: date ? `${date} log · Photos` : "Daily log · Photos" },
    { id: "gallery", label: "Gallery only", sub: integration === "local" ? "Led AI Photos" : "Procore Photos" },
  ];
}

/** The collapsed card's outcome words. */
function sentLabel(kind: "log" | "gallery", integration: WalkPhotosIntegration): string {
  if (integration === "autodesk") return `SENT · ${kind === "gallery" ? "FILES ONLY" : "DAILY REPORT + FILES"}`;
  return `${integration === "local" ? "SAVED" : "SENT"} · ${kind === "gallery" ? "GALLERY ONLY" : "DAILY LOG + GALLERY"}`;
}

// ── the draft ────────────────────────────────────────────────────────────

/** Left-out photos (`excluded`) — owned by the queue so a re-read that
 *  remounts this card keeps them. `removed` (delete outright) is the older
 *  pane's and stays for the submission's shape. */
export type WalkPhotosDraft = { removed: Record<string, true>; excluded?: Record<string, true> };
export const EMPTY_WALK_PHOTOS_DRAFT: WalkPhotosDraft = { removed: {}, excluded: {} };

export interface WalkPhotosPaneProps {
  visitId: string;
  /** The page's query string — workspace scope rides on it. */
  search: string;
  /** After Accept, the trash or Later landed: the queue re-reads the walk. */
  onSent: () => void;
  /** After Make it a (or its Undo) landed: the queue re-reads the walk, which
   *  now holds (or no longer holds) the new record's card. */
  onConverted: () => void;
  draft: WalkPhotosDraft;
  onDraftChange: (update: (draft: WalkPhotosDraft) => WalkPhotosDraft) => void;
  /**
   * The Photos cards this card decides (the walk's Photos-card rows, pending
   * or saved for later). A pending Photos card outside it — an older one
   * (LED-711) — keeps its photos out of NOT ATTACHED, and Accept leaves that
   * card alone. Absent: every pending Photos card on the walk.
   */
  photoCardIds?: readonly string[];
  /** The step's Photos cards are saved for later: the card starts collapsed. */
  saved?: boolean;
  /** The walk's project integration; absent for a project with no link. */
  syncSource?: SyncSource;
  /** Bumped by the queue after any decision, so a record rejected (which puts
   *  its photo back) or accepted shows here without a reload. */
  refreshKey?: number;
  /** The viewer's OPEN → on a photo already on a record. */
  hrefForItem?: (itemId: string) => string;
  onOpenItem?: (itemId: string) => void;
}

type Toast = { recordId: string; mediaId: string; kind: WalkPhotoConvertKind; label: string };

export function WalkPhotosPane({
  visitId,
  search,
  onSent,
  onConverted,
  draft,
  onDraftChange: setDraft,
  photoCardIds,
  saved,
  syncSource,
  refreshKey = 0,
  hrefForItem,
  onOpenItem,
}: WalkPhotosPaneProps) {
  const [reload, setReload] = useState(0);
  const { data, failure } = useResource(
    `walk-photos:${visitId}${search}`,
    () => fetchWalkPhotos(visitId, search),
    reload + refreshKey,
  );
  /** The photo open in the full-screen viewer. */
  const [viewing, setViewing] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, Partial<Pick<ConsoleWalkPhoto, "description" | "location">>>>({});
  const [busy, setBusy] = useState<"send" | "skip" | "later" | "delete" | null>(null);
  const [converting, setConverting] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [destinationPick, setDestinationPick] = useState<WalkPhotoDestination | null>(null);
  /** Photos waiting on the delete confirmation (unlinked projects). */
  const [confirmDelete, setConfirmDelete] = useState<{ mediaIds: string[]; fromViewer: boolean } | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const outcome = useWalkPhotosOutcome(visitId);
  const [reopened, setReopened] = useState(false);

  useEffect(() => {
    setEdits({});
    setViewing(null);
    setDestinationPick(null);
    setReopened(false);
    setToast(null);
  }, [visitId]);

  const excluded = draft.excluded ?? {};
  const photos = useMemo(
    () => (data?.photos ?? []).map((photo) => ({ ...photo, ...edits[photo.mediaId] })),
    [data, edits],
  );
  const scopeKey = photoCardIds ? photoCardIds.join(",") : null;
  const underReview = useMemo(() => {
    const scope = scopeKey === null ? null : new Set(scopeKey.split(",").filter(Boolean));
    return (photo: ConsoleWalkPhoto) => isReviewableWalkPhoto(photo) && (!scope || scope.has(photo.home!.itemId));
  }, [scopeKey]);
  const live = photos.filter((photo) => !draft.removed[photo.mediaId]);
  const mine = live.filter(underReview);
  const loose = mine.filter((photo) => !excluded[photo.mediaId]);
  const leftOut = mine.filter((photo) => excluded[photo.mediaId]);
  const attached = live.filter((photo) => photo.home && !underReview(photo));
  /** The viewer pages through the card's photos in its order. */
  const lane = useMemo(() => [...loose, ...attached], [loose, attached]);
  const isLoose = (photo: ConsoleWalkPhoto) => underReview(photo) && !excluded[photo.mediaId];

  const writable = !!data?.writesConfigured;
  const integration = walkPhotosIntegrationFor(syncSource);
  const acc = integration === "autodesk";
  const provider = integrationName(syncSource);
  const destination: WalkPhotoDestination = destinationPick ?? walkPhotosDefaultDestination(data?.userRole);

  // A viewed photo that left the card (deleted, or gone in a re-read) closes it.
  useEffect(() => {
    if (viewing && data && !lane.some((photo) => photo.mediaId === viewing)) setViewing(null);
  }, [viewing, data, lane]);

  // The toast goes by itself.
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 8000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  /** The photo after `mediaId` in the viewer once it has left the lane. */
  const nextInLane = (mediaId: string): string | null => {
    const i = lane.findIndex((photo) => photo.mediaId === mediaId);
    const rest = lane.filter((photo) => photo.mediaId !== mediaId);
    if (rest.length === 0 || i < 0) return null;
    return rest[Math.min(i, rest.length - 1)]!.mediaId;
  };

  // What this card knows about the records made from its photos, for their
  // cards above (`WalkPhotoOrigin`). Re-published on every read.
  const undoRef = useRef<(recordId: string) => Promise<string | null>>(async () => null);
  undoRef.current = async (recordId: string) => {
    const result = await undoWalkPhotoConvert(visitId, search, recordId);
    if (!result.ok) return result.error ?? "Couldn’t put the photo back.";
    forgetWalkPhotoOrigin(recordId);
    setReload((n) => n + 1);
    onConverted();
    return null;
  };
  useEffect(() => {
    if (!data) return;
    const origins: WalkPhotoOrigin[] = (data.photos ?? []).flatMap((photo) =>
      photo.convertedTo && photo.home && !photo.home.approved
        ? [{
            recordId: photo.convertedTo.recordId,
            visitId,
            mediaId: photo.mediaId,
            kind: photo.convertedTo.recordKind,
            photoUrl: photo.thumbnailUrl ?? photo.url,
            photoTime: walkPhotoTileTime(photo.timestamp),
            undo: () => undoRef.current(photo.convertedTo!.recordId),
          }]
        : [],
    );
    publishWalkPhotoOrigins(visitId, origins);
  }, [data, visitId]);

  const saveDescription = async (photo: ConsoleWalkPhoto, text: string) => {
    if (text === photo.description || !text) return;
    const previous = photo.description;
    setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], description: text } }));
    const result = await updateWalkPhotoDescription(visitId, photo.mediaId, search, text);
    if (!result.ok) {
      setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], description: previous } }));
      setProblem(result.error ?? "Couldn’t save the description.");
    }
  };

  /** `null` is "No location". */
  const saveLocation = async (photo: ConsoleWalkPhoto, location: WalkPhotoLocation | null) => {
    const previous = photo.location;
    if (!location && !previous) return;
    setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], location: location ?? undefined } }));
    const result = await updateWalkPhotoLocation(visitId, photo.mediaId, search, location);
    if (!result.ok) {
      setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], location: previous } }));
      setProblem(result.error ?? "Couldn’t save the location.");
    }
  };

  const convert = async (photo: ConsoleWalkPhoto, kind: WalkPhotoConvertKind, section?: { key: string; label: string }) => {
    if (converting || !writable) return;
    setProblem(null);
    setConverting(photo.mediaId);
    const result = await convertWalkPhoto(visitId, photo.mediaId, search, kind, {
      title: newRecordTitle(kind, section?.label),
      ...(section ? { section: section.key } : {}),
    });
    setConverting(null);
    if (!result.ok || !result.result?.itemId) {
      setProblem(result.error ?? "Couldn’t make a record from this photo. Try again.");
      return;
    }
    const recordId = result.result.itemId;
    // Announce it before the queue's re-read lands, so its card arrives
    // already wearing "NEW … FROM PHOTO" and the green outline.
    publishWalkPhotoOrigins(visitId, [
      ...photos.flatMap((p) =>
        p.convertedTo && p.home && !p.home.approved
          ? [{ recordId: p.convertedTo.recordId, visitId, mediaId: p.mediaId, kind: p.convertedTo.recordKind, photoUrl: p.thumbnailUrl ?? p.url, photoTime: walkPhotoTileTime(p.timestamp), undo: () => undoRef.current(p.convertedTo!.recordId) }]
          : [],
      ),
      {
        recordId,
        visitId,
        mediaId: photo.mediaId,
        kind,
        photoUrl: photo.thumbnailUrl ?? photo.url,
        photoTime: walkPhotoTileTime(photo.timestamp),
        freshPending: true,
        undo: () => undoRef.current(recordId),
      },
    ]);
    // The design: the viewer closes, the toast says where the photo went.
    setViewing(null);
    setToast({ recordId, mediaId: photo.mediaId, kind, label: convertedLabel(kind, integration, section?.label) });
    setReload((n) => n + 1);
    onConverted();
  };

  const undoToast = async () => {
    if (!toast) return;
    const { recordId, mediaId } = toast;
    setToast(null);
    const failed = await undoRef.current(recordId);
    if (failed) {
      setProblem(failed);
      return;
    }
    // The design reopens the viewer on the photo that came back.
    setViewing(mediaId);
  };

  const deleteInFlight = useRef(false);
  /** Unlinked projects only: the trash asks first (`ConfirmDialog`). */
  const requestDelete = (mediaIds: string[], fromViewer = false) => {
    if (busy || converting || deleteInFlight.current || !writable || provider || mediaIds.length === 0) return;
    setConfirmDelete({ mediaIds, fromViewer });
  };
  const deletePhotos = async (mediaIds: string[], fromViewer: boolean) => {
    if (busy || converting || deleteInFlight.current || !writable || provider || mediaIds.length === 0) return;
    deleteInFlight.current = true;
    setBusy("delete");
    setProblem(null);
    const after = fromViewer && mediaIds.length === 1 ? nextInLane(mediaIds[0]!) : null;
    try {
      const result = await submitWalkPhotos(visitId, search, {
        removedMediaIds: mediaIds,
        conversions: [],
        deleteOnly: true,
        photoCardIds: photoCardIds ? [...photoCardIds] : [...new Set(mine.map((photo) => photo.home!.itemId))],
      });
      if (!result.ok || !result.result?.ok) {
        setProblem(result.error ?? "Some photos couldn't be deleted. They remain available to retry.");
      } else {
        setViewing(after);
      }
      setReload((n) => n + 1);
      // Refresh pending cards without declaring this walk sent: another photo
      // may still be waiting for review, including after a partial failure.
      onConverted();
    } catch {
      setProblem("Couldn't delete the photos. Try again.");
    } finally {
      deleteInFlight.current = false;
      setBusy(null);
      setConfirmDelete(null);
    }
  };

  /** Linked projects: MinusCircle keeps the photo in Led AI, out of the send. */
  const leaveOut = (mediaId: string, fromViewer = false) => {
    if (fromViewer) setViewing(nextInLane(mediaId));
    setDraft((d) => ({ ...d, excluded: { ...(d.excluded ?? {}), [mediaId]: true } }));
  };

  const decide = async (kind: "send" | "skip" | "later") => {
    if (busy || !data || !writable) return;
    setBusy(kind);
    setProblem(null);
    const cardIds = photoCardIds ? [...photoCardIds] : undefined;
    const count = loose.length;
    if (kind === "later") {
      // The phone's Later: the step's Photos cards are saved for later.
      const ids = cardIds ?? [...new Set(mine.map((photo) => photo.home!.itemId))];
      const results = await Promise.all(ids.map((id) => postReviewDecision("log", id, search, "deferred")));
      setBusy(null);
      if (results.some((r) => !r.ok)) {
        setProblem(results.find((r) => !r.ok)?.error ?? "Couldn’t save these photos for later. Try again.");
        return;
      }
      setWalkPhotosOutcome(visitId, { kind: "later", count, integration });
      setReopened(false);
      onSent();
      return;
    }
    // MinusCircle sends none: every photo on the cards is kept in Led AI.
    const leaveOutIds = kind === "skip" ? mine.map((photo) => photo.mediaId) : leftOut.map((photo) => photo.mediaId);
    const result = await submitWalkPhotos(visitId, search, {
      removedMediaIds: Object.keys(draft.removed),
      excludedMediaIds: leaveOutIds,
      conversions: [],
      ...(cardIds ? { photoCardIds: cardIds } : {}),
      // Autodesk too since a81a1a055: gallery only is Autodesk Files.
      ...(kind === "send" ? { destination } : {}),
    });
    setBusy(null);
    if (!result.ok) {
      setProblem(result.error ?? "Couldn’t send the walk photos. Try again.");
      return;
    }
    const failed = result.result?.failedMediaIds ?? [];
    if (failed.length > 0 || result.result?.ok === false) {
      const n = failed.length;
      setProblem(
        n === 0
          ? "Couldn’t send the walk photos. Try again."
          : n === 1
            ? "1 photo couldn’t be updated, so the card wasn’t sent. Try again."
            : `${n} photos couldn’t be updated, so the card wasn’t sent. Try again.`,
      );
      setReload((r) => r + 1);
      return;
    }
    setDraft(() => EMPTY_WALK_PHOTOS_DRAFT);
    setWalkPhotosOutcome(visitId, { kind: kind === "skip" ? "not-sent" : destination === "gallery" ? "gallery" : "log", count, integration });
    onSent();
  };

  const toastNode = toast ? <ConvertToast toast={toast} onUndo={() => void undoToast()} /> : null;
  const deleteCount = confirmDelete?.mediaIds.length ?? 0;
  const dialogNode = confirmDelete ? (
    <ConfirmDialog
      testId="walk-photos-delete-dialog"
      title={deleteCount === 1 ? "Delete photo?" : "Delete photos?"}
      message={deleteCount === 1 ? "Are you sure you want to delete this photo?" : `Are you sure you want to delete all ${deleteCount} photos?`}
      detail={`This removes ${deleteCount === 1 ? "the photo" : "these photos"} from Led AI Photos and cannot be undone.`}
      confirmLabel="Delete"
      busyLabel="Deleting…"
      destructive
      busy={busy === "delete"}
      onCancel={() => setConfirmDelete(null)}
      onConfirm={() => void deletePhotos(confirmDelete.mediaIds, confirmDelete.fromViewer)}
    />
  ) : null;

  // ── collapsed ──────────────────────────────────────────────────────────
  const collapsed: WalkPhotosOutcome | null =
    outcome && !(outcome.kind === "later" && reopened)
      ? outcome
      : saved && !reopened
        ? { kind: "later", count: loose.length }
        : null;
  if (collapsed) {
    const tone =
      collapsed.kind === "not-sent"
        ? { bg: WPC.red, fg: WPC.redInk, icon: <X size={10} color={WPC.white} strokeWidth={3} aria-hidden />, label: "NOT SENT" }
        : collapsed.kind === "later"
          ? { bg: WPC.quiet, fg: WPC.label, icon: <List size={10} color={WPC.white} strokeWidth={3} aria-hidden />, label: "SAVED FOR LATER" }
          : {
              bg: WPC.green,
              fg: WPC.greenInk,
              icon: <Check size={10} color={WPC.white} strokeWidth={3} aria-hidden />,
              label: sentLabel(collapsed.kind === "gallery" ? "gallery" : "log", collapsed.integration ?? integration),
            };
    const reopen = collapsed.kind === "later";
    return (
      <div data-testid="walk-photos-card" data-state={collapsed.kind} style={{ ...cardStyle, fontFamily: FONT_TEXT }}>
        <div
          data-testid="walk-photos-collapsed"
          role={reopen ? "button" : undefined}
          tabIndex={reopen ? 0 : undefined}
          title={reopen ? "Open the walk photos" : undefined}
          onClick={reopen ? () => { setReopened(true); setWalkPhotosOutcome(visitId, null); } : undefined}
          onKeyDown={reopen ? (e) => { if (e.key === "Enter") { setReopened(true); setWalkPhotosOutcome(visitId, null); } } : undefined}
          style={{ display: "flex", alignItems: "center", gap: 10, height: 52, padding: "0 18px", cursor: reopen ? "pointer" : "default" }}
        >
          <span style={{ flex: "none", width: 20, height: 20, borderRadius: 10, background: tone.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{tone.icon}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: WPC.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {walkPhotosCardTitle(collapsed.count)}
          </span>
          <span data-testid="walk-photos-outcome" style={{ ...MONO, flex: "none", fontSize: 9, letterSpacing: "1.1px", color: tone.fg }}>{tone.label}</span>
        </div>
        {toastNode}
      </div>
    );
  }

  if (failure) {
    return (
      <div data-testid="walk-photos-card" style={{ ...cardStyle, padding: "18px", fontFamily: FONT_TEXT, fontSize: 14, color: WPC.sub }}>
        <span data-testid="walk-photos-failure">Couldn’t load this walk’s photos. </span>
        <button type="button" onClick={() => setReload((n) => n + 1)} style={linkButton}>Try again</button>
      </div>
    );
  }

  const day = data?.walkStartedAt ?? null;
  const lastPhoto = mine.length ? mine[mine.length - 1]!.timestamp : photos[photos.length - 1]?.timestamp;
  const dests = walkPhotosDestinations(integration, day);
  const locked = !!busy || !!converting || !data || !writable;
  const viewed = viewing ? lane.find((photo) => photo.mediaId === viewing) ?? null : null;

  return (
    <div data-testid="walk-photos-card" data-state="open" data-integration={integration} style={{ ...cardStyle, fontFamily: FONT_TEXT, color: WPC.ink }}>
      <style>{`.wpc-press{transition:transform .14s ease}.wpc-press:active:not(:disabled){transform:scale(.97)}
.wpc-trash:hover:not(:disabled){background:${WPC.red}!important}
.wpc-chip:hover:not(:disabled){border-color:rgba(20,17,12,.3)!important}
.wpc-sec:hover:not(:disabled){background:${WPC.hover}!important}
.wpc-out:hover:not(:disabled){background:${WPC.punchTagBg}!important}
.wpc-desc:empty:before{content:attr(data-ph);color:${WPC.quiet}}
.wpc-rec{transition:opacity .14s}.wpc-rec:hover{opacity:1!important}
.wpc-tile:focus-visible,.wpc-press:focus-visible,.wpc-rec:focus-visible{outline:2px solid ${WPC.ink};outline-offset:2px}`}</style>
      <div style={{ padding: "16px 18px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 26, padding: "0 10px", borderRadius: 999, background: WPC.pill, color: WPC.pillInk }}>
            <Camera size={11} color={WPC.pillInk} strokeWidth={2.2} aria-hidden />
            <span style={{ ...MONO, fontSize: 9, letterSpacing: "1.4px" }}>Walk photos</span>
          </span>
          <span data-testid="walk-photos-stamp" style={{ ...MONO, fontWeight: 600, fontSize: 9, letterSpacing: "1.2px", color: WPC.label, whiteSpace: "nowrap" }}>
            {stampOf(day, lastPhoto)}
          </span>
        </div>
        <div data-testid="walk-photos-title" style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-.5px", marginTop: 10 }}>
          {data ? walkPhotosCardTitle(loose.length) : "Walk photos"}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 14 }}>
          <span style={LABEL}>{`Not attached · ${data ? loose.length : "–"}`}</span>
          {leftOut.length > 0 ? (
            <button
              type="button"
              data-testid="walk-photos-restore"
              disabled={locked}
              onClick={() => setDraft((d) => ({ ...d, excluded: {} }))}
              style={{ ...LABEL, color: WPC.ink, border: "none", background: "none", padding: 0, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
            >
              {`Restore ${leftOut.length}`}
            </button>
          ) : null}
        </div>
        {!data ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8, marginTop: 8 }}>
            {[0, 1, 2, 3].map((i) => <SkeletonBlock key={i} height={120} radius={10} />)}
          </div>
        ) : loose.length === 0 ? (
          <div data-testid="walk-photos-none" style={{ padding: "12px 0 2px", fontSize: 13.5, lineHeight: 1.5, color: WPC.sub }}>
            {mine.length > 0
              ? `Every photo is left out. Accept sends nothing; they stay in Led AI.`
              : "Every photo from this walk is on a record."}
          </div>
        ) : (
          <div data-testid="walk-photos-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 8, marginTop: 8 }}>
            {loose.map((photo) => {
              const src = photo.thumbnailUrl ?? photo.url;
              return (
                <div
                  key={photo.mediaId}
                  data-testid="walk-photos-tile"
                  data-media-id={photo.mediaId}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  aria-label={`Open photo ${walkPhotoTileTime(photo.timestamp)}`}
                  className="wpc-tile"
                  onClick={() => setViewing(photo.mediaId)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setViewing(photo.mediaId); } }}
                  style={{ position: "relative", aspectRatio: "1", borderRadius: 10, overflow: "hidden", background: WPC.tile, cursor: "zoom-in" }}
                >
                  {src ? <PhotoImage src={src} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : null}
                  {converting === photo.mediaId ? (
                    <span style={{ ...MONO, position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,253,248,.78)", fontSize: 9, letterSpacing: "1.2px", color: WPC.sub }}>Making it…</span>
                  ) : null}
                  <span style={{ ...MONO, position: "absolute", left: 6, bottom: 6, fontSize: 8.5, color: WPC.white, background: "rgba(10,10,10,.6)", padding: "2px 5px", borderRadius: 4 }}>
                    {walkPhotoTileTime(photo.timestamp)}
                  </span>
                  {writable ? (
                    <button
                      type="button"
                      data-testid="walk-photos-leave-out"
                      aria-label={provider ? "Leave this photo out" : "Delete photo"}
                      title={provider ? "Don't send this photo" : "Delete photo"}
                      className="wpc-trash"
                      disabled={locked}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!provider) requestDelete([photo.mediaId]);
                        else leaveOut(photo.mediaId);
                      }}
                      style={{ position: "absolute", right: 6, top: 6, width: 26, height: 26, padding: 0, border: "none", borderRadius: 13, background: "rgba(10,10,10,.6)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "background .14s" }}
                    >
                      {provider ? <MinusCircle size={16} color={WPC.white} aria-hidden /> : <Trash2 size={14} color={WPC.white} aria-hidden />}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {attached.length > 0 ? (
          <>
            <div style={{ ...LABEL, marginTop: 16 }}>{`Already on records · ${attached.length}`}</div>
            <div data-testid="walk-photos-attached" style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              {attached.map((photo) => {
                const tag = recordTagOf(photo);
                const isNew = !!photo.convertedTo && !photo.home?.approved;
                const src = photo.thumbnailUrl ?? photo.url;
                return (
                  <div key={photo.mediaId} data-testid="walk-photos-on-record" data-media-id={photo.mediaId} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 5 }}>
                    <button
                      type="button"
                      data-testid="walk-photos-on-record-open"
                      aria-haspopup="dialog"
                      aria-label={`Open photo on ${photo.home?.title || "a record"}`}
                      title={photo.home?.title}
                      className="wpc-rec"
                      onClick={() => setViewing(photo.mediaId)}
                      style={{ position: "relative", width: 64, height: 64, padding: 0, border: "none", borderRadius: 9, overflow: "hidden", background: WPC.tile, display: "block", opacity: 0.85, cursor: "zoom-in" }}
                    >
                      {src ? <PhotoImage src={src} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : null}
                    </button>
                    <span data-testid="walk-photos-tag" style={{ ...MONO, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 8, letterSpacing: ".9px", color: RECORD_TONE[tag].fg, background: RECORD_TONE[tag].bg, padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>
                      <RecordIcon tag={tag} size={8} color={RECORD_TONE[tag].fg} strokeWidth={2.8} />
                      {isNew ? `New ${tag}` : tag}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}

        <div role="radiogroup" aria-label="Where these photos go" style={{ display: "flex", gap: 4, marginTop: 16, padding: 4, borderRadius: 14, background: WPC.pill }}>
          {dests.map((d) => {
            const on = destination === d.id;
            return (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={on}
                data-testid={`walk-photos-dest-${d.id}`}
                disabled={!!busy}
                onClick={() => setDestinationPick(d.id)}
                style={{ flex: 1, minWidth: 0, padding: "9px 12px", borderRadius: 10, border: "none", background: on ? WPC.white : "transparent", boxShadow: on ? "0 1px 3px rgba(20,17,12,.12)" : "none", cursor: "pointer", textAlign: "left", fontFamily: FONT_TEXT, transition: "background .14s, box-shadow .14s" }}
              >
                <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: on ? WPC.ink : WPC.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.label}</span>
                <span style={{ display: "block", fontSize: 11.5, color: WPC.label, marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.sub}</span>
              </button>
            );
          })}
        </div>
        {problem ? (
          <div role="alert" data-testid="walk-photos-problem" style={{ marginTop: 10, fontSize: 13, lineHeight: 1.4, color: WPC.redInk }}>{problem}</div>
        ) : null}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, padding: "12px 18px 14px", borderTop: HAIRLINE }}>
        <button
          type="button"
          data-testid="walk-photos-later"
          className="wpc-press"
          disabled={locked}
          onClick={() => void decide("later")}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 38, padding: "0 15px", borderRadius: 19, border: OUTLINE, background: WPC.white, fontFamily: FONT_TEXT, fontSize: 13.5, fontWeight: 700, color: WPC.black, cursor: locked ? "default" : "pointer", opacity: busy === "later" ? 0.6 : 1 }}
        >
          <List size={12} color={WPC.black} strokeWidth={2.4} aria-hidden />
          {busy === "later" ? "Saving…" : "Later"}
        </button>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="walk-photos-skip"
          aria-label={provider ? "Don't send these photos" : "Delete photos"}
          title={provider ? "Don't send these photos" : "Delete photos"}
          className="wpc-press wpc-out"
          disabled={locked || (!provider && mine.length === 0)}
          // Unlinked: every photo not on a record — the loose ones and any
          // left out — as the phone's footer trash takes them.
          onClick={() => provider ? void decide("skip") : requestDelete(mine.map((photo) => photo.mediaId))}
          style={{ width: 38, height: 38, padding: 0, borderRadius: 19, border: OUTLINE, background: WPC.white, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: locked ? "default" : "pointer", opacity: busy === "skip" ? 0.6 : 1, transition: "background .14s" }}
        >
          {provider ? <MinusCircle size={16} color={WPC.black} aria-hidden /> : <Trash2 size={14} color={WPC.black} aria-hidden />}
        </button>
        <button
          type="button"
          data-testid="walk-photos-send"
          className="wpc-press"
          disabled={locked}
          onClick={() => void decide("send")}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 38, padding: "0 18px", borderRadius: 19, border: "none", background: WPC.black, color: WPC.white, fontFamily: FONT_TEXT, fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", cursor: locked ? "default" : "pointer", opacity: locked && busy !== "send" ? 0.55 : busy === "send" ? 0.7 : 1 }}
        >
          <Check size={12} color={WPC.white} strokeWidth={2.6} aria-hidden />
          {busy === "send" ? (provider ? "Sending…" : "Saving…") : (provider ? "Accept" : "Save")}
        </button>
      </div>

      {viewed && data ? (
        <WalkPhotoViewer
          lane={lane}
          mediaId={viewed.mediaId}
          integration={integration}
          visitId={visitId}
          search={search}
          jobSiteId={data.jobSiteId}
          walkName={data.walkName}
          projectName={data.projectName}
          writable={writable}
          converting={!!converting}
          logSections={LOG_SECTIONS}
          isLoose={isLoose}
          problem={problem}
          recordHref={hrefForItem}
          onOpenRecord={onOpenItem}
          onPick={(id) => { setProblem(null); setViewing(id); }}
          onClose={() => setViewing(null)}
          onSaveDescription={(photo, text) => void saveDescription(photo, text)}
          onSaveLocation={(photo, location) => void saveLocation(photo, location)}
          onConvert={(photo, kind, section) => void convert(photo, kind, section)}
          onLeaveOut={(photo) => (provider ? leaveOut(photo.mediaId, true) : requestDelete([photo.mediaId], true))}
        />
      ) : null}
      {dialogNode}
      {toastNode}
    </div>
  );
}

/** "Photo is now a new punch item — it moved to Already on records" · UNDO.
 *  Bottom-centre, dark, as the design draws it after a Make it a. */
function ConvertToast({ toast, onUndo }: { toast: Toast; onUndo: () => void }) {
  return createPortal(
    <div role="status" aria-live="polite" style={{ position: "fixed", left: "50%", bottom: 28, transform: "translateX(-50%)", zIndex: 1050, maxWidth: "calc(100vw - 32px)" }}>
      <style>{"@keyframes wpcToast{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}"}</style>
      <div
        data-testid="walk-photos-toast"
        style={{ display: "inline-flex", alignItems: "center", gap: 10, minHeight: 44, padding: "0 16px 0 12px", borderRadius: 22, background: WPC.ink, color: WPC.white, boxShadow: "0 14px 34px rgba(10,10,10,.25)", fontFamily: FONT_TEXT, animation: "wpcToast .3s both" }}
      >
        <span style={{ flex: "none", width: 22, height: 22, borderRadius: 11, background: WPV.gold, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
          <RecordIcon tag={recordTagOfKind(toast.kind)} size={12} color={WPC.ink} strokeWidth={2.6} />
        </span>
        <span data-testid="walk-photos-toast-text" style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, padding: "6px 0" }}>
          {`Photo is now a new ${toast.label} — it moved to Already on records`}
        </span>
        <button
          type="button"
          data-testid="walk-photos-toast-undo"
          onClick={onUndo}
          style={{ ...MONO, flex: "none", marginLeft: 6, padding: 0, border: "none", background: "none", fontSize: 9.5, letterSpacing: "1.2px", color: WPV.gold, cursor: "pointer" }}
        >
          Undo
        </button>
      </div>
    </div>,
    document.body,
  );
}

const cardStyle: CSSProperties = {
  background: WPC.card,
  borderRadius: 18,
  boxShadow: CARD_SHADOW,
  // NOT hidden: the location menu is positioned against the card and has to
  // escape it.
  overflow: "visible",
};

const linkButton: CSSProperties = { border: "none", background: "none", padding: 0, color: WPC.ink, fontWeight: 700, fontSize: "inherit", fontFamily: "inherit", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 };

function MenuButton(props: { onClick: () => void; active?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      role="menuitem"
      className="wpc-sec"
      onClick={(e) => {
        e.stopPropagation();
        props.onClick();
      }}
      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", height: 36, padding: "0 10px", borderRadius: 8, border: "none", background: props.active ? WPC.hover : "transparent", fontFamily: FONT_TEXT, fontSize: 13.5, fontWeight: 600, color: WPC.ink, cursor: "pointer", textAlign: "left", whiteSpace: "nowrap" }}
    >
      {props.children}
    </button>
  );
}

/** The project's locations (the console's location directory), loaded once
 *  per open and filtered as you type; a typed name is kept as free text. */
export function LocationMenu(props: {
  current?: WalkPhotoLocation;
  jobSiteId: string;
  visitId: string;
  search: string;
  onPick: (location: WalkPhotoLocation) => void;
  onClose: () => void;
}) {
  const { jobSiteId, visitId, search, onClose } = props;
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<ItemEditDirectoryOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchItemEditOptions(jobSiteId, "photos", search, visitId)
      .then((found) => !cancelled && setOptions(found.locations))
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
          setFailed(true);
        }
      });
    input.current?.focus();
    return () => {
      cancelled = true;
    };
  }, [jobSiteId, visitId, search]);

  // Click outside or Escape closes it.
  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const timer = window.setTimeout(() => document.addEventListener("mousedown", down), 0);
    document.addEventListener("keydown", key);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
    };
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const shown = (options ?? []).filter((o) => !q || `${o.path ?? ""} ${o.name}`.toLowerCase().includes(q));
  const typed = query.trim();

  return (
    <div ref={box} data-testid="walk-photos-location-menu" style={{ position: "absolute", left: -6, width: 300, maxWidth: "calc(100vw - 48px)", top: "calc(100% + 6px)", zIndex: 40, background: WPC.white, borderRadius: 14, boxShadow: POP, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 12px", borderBottom: "1px solid rgba(10,10,10,.08)" }}>
        <Search size={13} color={WPC.label} strokeWidth={2} aria-hidden />
        <input
          ref={input}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && typed && shown.length === 0) props.onPick({ name: typed, freeText: true }); }}
          placeholder="Search locations"
          aria-label="Search locations"
          style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: FONT_TEXT, fontSize: 13.5, fontWeight: 600, color: WPC.ink }}
        />
      </div>
      <div style={{ maxHeight: 220, overflowY: "auto", padding: 4 }}>
        {options === null ? (
          <div style={{ padding: 10, fontSize: 13, color: WPC.label }}>Loading…</div>
        ) : (
          shown.map((o) => {
            const on = props.current?.procoreLocationId === o.remoteId;
            const { pre, leaf } = splitPath({ name: o.name, path: o.path });
            return (
              <MenuButton
                key={o.remoteId}
                active={on}
                onClick={() => props.onPick({ name: o.name, ...(o.path ? { path: o.path } : {}), procoreLocationId: o.remoteId, freeText: false })}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ color: WPC.label, fontWeight: 500 }}>{pre}</span>
                  {leaf}
                </span>
                {on ? <Check size={13} color={WPC.ink} strokeWidth={2.4} aria-hidden /> : null}
              </MenuButton>
            );
          })
        )}
        {options !== null && shown.length === 0 ? (
          typed ? (
            <MenuButton onClick={() => props.onPick({ name: typed, freeText: true })}>
              <span>Use “{typed}”</span>
            </MenuButton>
          ) : (
            <div style={{ padding: 10, fontSize: 13, color: WPC.label }}>{failed ? "Locations are unavailable." : "No locations yet. Type one to use it."}</div>
          )
        ) : null}
      </div>
    </div>
  );
}
